<template>
  <div class="scan-animals-view">
    <div class="audit-container">
      <div class="view-header">
        <h1 class="view-title">Scan Animals</h1>
      </div>

      <div class="main-content-layout">
        <div class="scanner-column">
          <div class="scanner-section">
            <BarcodeScanner ref="barcodeScannerRef" @scan="handleScan" @error="handleScanError" />
          </div>

          <div class="status-container">
            <div v-if="scanError" class="status-card error-banner">
              <div class="status-icon">
                <i class="pi pi-exclamation-triangle"></i>
              </div>
              <div class="status-content">
                <strong class="status-title">Error</strong>
                <span class="status-message">{{ scanError }}</span>
              </div>
            </div>

            <div v-if="duplicateError" class="status-card warning-banner">
              <div class="status-icon">
                <i class="pi pi-info-circle"></i>
              </div>
              <div class="status-content">
                <strong class="status-title">Already Scanned</strong>
                <span class="status-message">{{ duplicateError }}</span>
              </div>
            </div>

            <div v-if="scanSuccess" class="status-card success-banner">
              <div class="status-icon">
                <i class="pi pi-check-circle"></i>
              </div>
              <div class="status-content">
                <strong class="status-title">Animal Scanned</strong>
                <span class="status-message">{{ scanSuccessMessage }}</span>
              </div>
            </div>
          </div>

          <div class="done-section">
            <button class="btn-done" @click="handleComplete">
              <i class="pi pi-check"></i>
              <span>Done</span>
            </button>
          </div>
        </div>

        <div class="animals-column">
          <AnimalScanList :expected-animals="expectedAnimals" :scanned-animals="scannedAnimals" />
        </div>
      </div>

      <div v-if="allExpectedScanned" class="completion-section">
        <RemainingAnimalsPrompt
          :show="showRemainingPrompt"
          :expected-count="expectedAnimals.length"
          @continue-scanning="handleContinueScanning"
          @complete="handleComplete"
          @cancel="handleCancelPrompt"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import BarcodeScanner from '../../components/audit/BarcodeScanner.vue';
import AnimalScanList from '../../components/audit/AnimalScanList.vue';
import RemainingAnimalsPrompt from '../../components/audit/RemainingAnimalsPrompt.vue';
import { useAuditStore } from '../../stores/audit.store';
import { validateScannedAnimal, areAllExpectedScanned } from '../../services/validation.service';

const router = useRouter();
const auditStore = useAuditStore();

const barcodeScannerRef = ref<InstanceType<typeof BarcodeScanner> | null>(null);
const scanError = ref<string | null>(null);
const duplicateError = ref<string | null>(null);
const scanSuccess = ref(false);
const scanSuccessMessage = ref('');

const expectedAnimals = computed(() => auditStore.expectedAnimals);
const scannedAnimals = computed(() => auditStore.scannedAnimals);
const allExpectedScanned = computed(() => {
  return areAllExpectedScanned(expectedAnimals.value, scannedAnimals.value);
});
const showRemainingPrompt = ref(false);

// Watch for completion and show prompt
watch(allExpectedScanned, (isComplete) => {
  if (isComplete && !showRemainingPrompt.value) {
    // Small delay to let user see the completion status
    setTimeout(() => {
      showRemainingPrompt.value = true;
    }, 1000);
  }
});

/**
 * Handle barcode scan
 */
function handleScan(barcode: string): void {
  // Clear previous errors
  scanError.value = null;
  duplicateError.value = null;
  scanSuccess.value = false;

  // Validate scanned animal
  const validation = validateScannedAnimal(barcode, expectedAnimals.value, scannedAnimals.value);

  if (!validation.isValid) {
    if (validation.isDuplicate) {
      duplicateError.value = validation.error || `Barcode ${barcode} has already been scanned.`;
      setTimeout(() => {
        duplicateError.value = null;
        if (barcodeScannerRef.value) {
          barcodeScannerRef.value.reset();
        }
      }, 3000);
    } else {
      scanError.value = validation.error || 'Invalid scan';
    }
    return;
  }

  // Add to scanned animals
  auditStore.addScannedAnimal(barcode, validation.isExpected, validation.expectedAnimal?.id);

  // Show success message
  scanSuccess.value = true;
  scanSuccessMessage.value = validation.isExpected
    ? `Expected animal ${barcode} scanned successfully.`
    : `Unexpected animal ${barcode} scanned.`;

  // Clear success message after 2 seconds
  setTimeout(() => {
    scanSuccess.value = false;
    if (barcodeScannerRef.value) {
      barcodeScannerRef.value.reset();
    }
  }, 2000);
}

/**
 * Handle scanner errors
 */
function handleScanError(message: string): void {
  scanError.value = message;
  scanSuccess.value = false;
  duplicateError.value = null;
}

/**
 * Handle audit completion
 */
function handleComplete(): void {
  // Navigate to completion view
  router.push({ name: 'audit-completion' });
}

/**
 * Handle continue scanning additional animals
 */
function handleContinueScanning(): void {
  showRemainingPrompt.value = false;
  // Scanner is already available, user can continue scanning
  if (barcodeScannerRef.value) {
    barcodeScannerRef.value.focus();
  }
}

/**
 * Handle cancel prompt
 */
function handleCancelPrompt(): void {
  showRemainingPrompt.value = false;
  // User can continue scanning or complete later
}

/**
 * Check if we have expected animals, if not redirect
 */
onMounted(async () => {
  console.info('ScanAnimalsView mounted', {
    expectedAnimalsCount: expectedAnimals.value.length,
    scannedAnimalsCount: scannedAnimals.value.length,
    scannedBarcodes: scannedAnimals.value.map((a) => a.rapidTagBarcode),
    expectedBarcodes: expectedAnimals.value.map((a) => a.rapidTagBarcode),
  });

  if (!expectedAnimals.value || expectedAnimals.value.length === 0) {
    // No expected animals, redirect to start
    router.push({ name: 'audit-start' });
    return;
  }

  // Focus scanner on mount
  await nextTick();
  setTimeout(() => {
    if (barcodeScannerRef.value) {
      barcodeScannerRef.value.focus();
    }
  }, 100);
});
</script>

<style scoped>
.scan-animals-view {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #f6f8fb 0%, #e5ebf1 100%);
  flex: 1;
  overflow: hidden;
}

.audit-container {
  background: white;
  border-radius: 1rem;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  width: 100%;
  max-width: 90%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.view-header {
  text-align: left;
  margin-bottom: 0.75rem;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  margin-bottom: 0.5rem;
}

.view-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.025em;
}

.view-description {
  color: #6b7280;
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.6;
  max-width: 100%;
  padding: 0 1rem;
}

.main-content-layout {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
}

.scanner-column {
  flex: 0 0 45%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.scanner-section {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.status-container {
  min-height: 5rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.status-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  font-size: 1.25rem;
}

.status-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-title {
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0;
}

.status-message {
  font-size: 0.875rem;
  opacity: 0.9;
  line-height: 1.4;
}

.error-banner {
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.error-banner .status-icon {
  background-color: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.warning-banner {
  background-color: #fef3c7;
  border-color: #fbbf24;
  color: #92400e;
}

.warning-banner .status-icon {
  background-color: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.success-banner {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.success-banner .status-icon {
  background-color: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.animals-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
}

.done-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-done {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 3rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-done:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.btn-done:active {
  transform: translateY(0);
}

.btn-done i {
  font-size: 1.25rem;
}

.done-hint {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
}

.completion-section {
  margin-top: 1rem;
}

.completion-card {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 2px solid #10b981;
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  animation: slideIn 0.3s ease-out;
}

.completion-icon {
  width: 4rem;
  height: 4rem;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
  color: #10b981;
}

.completion-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #065f46;
  margin: 0 0 0.5rem 0;
}

.completion-message {
  font-size: 1rem;
  color: #047857;
  margin: 0 0 1.5rem 0;
}

.btn-complete {
  padding: 1rem 2rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-complete:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}

.btn-complete:active {
  transform: translateY(0);
}

/* Tablet/iPad specific styles */
@media (min-width: 768px) and (max-width: 1024px) {
  .scan-animals-view {
    padding: 0.5rem;
    height: calc(100vh - 120px);
    overflow: hidden;
  }

  .audit-container {
    padding: 0.75rem;
    gap: 0.5rem;
    max-width: 98%;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .view-header {
    margin-bottom: 0.5rem;
    flex-shrink: 0;
  }

  .view-title {
    font-size: 1.25rem;
    margin: 0;
  }

  .main-content-layout {
    flex: 1;
    gap: 0.75rem;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
    display: flex;
  }

  .scanner-column {
    flex: 0 0 42%;
    gap: 0.5rem;
  }

  .scanner-section {
    padding: 0;
    flex-shrink: 0;
  }

  .status-container {
    min-height: 0;
    gap: 0.375rem;
    flex-shrink: 0;
  }

  .status-card {
    padding: 0.5rem 0.625rem;
    gap: 0.5rem;
  }

  .status-icon {
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.875rem;
  }

  .status-title {
    font-size: 0.75rem;
  }

  .status-message {
    font-size: 0.6875rem;
  }

  .animals-column {
    flex: 1;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .done-section {
    margin-top: auto;
    padding-top: 0.5rem;
    gap: 0.25rem;
    border-top: 1px solid #e5e7eb;
  }

  .btn-done {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
    width: 100%;
  }

  .btn-done i {
    font-size: 0.9375rem;
  }

  .done-hint {
    display: none;
  }
}

@media (max-width: 768px) {
  .audit-container {
    padding: 2rem;
    gap: 1.75rem;
    border-radius: 0.875rem;
    max-width: 95%;
    min-height: 65vh;
  }

  .icon-wrapper {
    width: 72px;
    height: 72px;
    font-size: 2.25rem;
    border-radius: 0.875rem;
  }

  .view-title {
    font-size: 2rem;
  }

  .view-description {
    font-size: 1.0625rem;
    padding: 0 0.5rem;
  }

  .scanner-section {
    padding: 1.25rem 0;
  }

  .status-container {
    min-height: 4.5rem;
  }

  .done-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
  }

  .btn-done {
    padding: 0.875rem 2rem;
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
  }

  .done-hint {
    font-size: 0.8125rem;
  }

  .completion-card {
    padding: 1.5rem;
  }

  .completion-icon {
    width: 3rem;
    height: 3rem;
    font-size: 1.5rem;
  }

  .completion-title {
    font-size: 1.25rem;
  }
}
</style>
